import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { useAppContext } from "@/context/AppContext";
import { useNavigation } from "@/context/NavigationContext";
import RentalProductCard from "@/components/AircraftRentalCard";
import InstrumentLoader from "@/components/InstrumentLoader";
import {
  Search,
  Filter,
  Plane,
  LayoutGrid,
  ShieldCheck,
  RotateCcw,
  Activity
} from "lucide-react";

interface AircraftRentalCategoryPageProps {
  title?: string;
  categories?: string[];
}

type SortOption = "rate-asc" | "rate-desc" | "speed-desc" | "newest";

interface CategoryOption {
  code: string;
  label: string;
}

interface CategoryGroup {
  label: string;
  options: CategoryOption[];
}

const AircraftRentalCategoryPage = ({ title }: AircraftRentalCategoryPageProps) => {
  const { navigateTo } = useNavigation();
  const { getDictValue, getDictJson } = useAuth();

  // 🟢 Extract rental state, fetch function, and loader directly from AppContext
  const {
    aircraftRentals,
    fetchAircraftRentals,
    isAircraftRentalsLoading
  } = useAppContext();

  // State-uri pentru filtre
  const [search, setSearch] = useState("");
  const [maxRate, setMaxRate] = useState<number | "">("");
  const [sort, setSort] = useState<SortOption>("newest");
  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>([]);
  const [selectedLicenses, setSelectedLicenses] = useState<string[]>([]);
  const [maxExperience, setMaxExperience] = useState<number | "">("");
  const [minSpeed, setMinSpeed] = useState<number | "">("");
  const [ifrOnly, setIfrOnly] = useState(false);
  const [selectedFuelTypes, setSelectedFuelTypes] = useState<string[]>([]);
  const [billingMethod, setBillingMethod] = useState<string>("");
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const fuelOptions = ["AVGAS 100LL", "MOGAS", "JET A1", "Diesel"];

  // 🟢 Preluare dinamică din dicționar folosind getDictJson
  const categoriesList = useMemo(() => {
    return getDictJson<CategoryGroup[]>("listing.rental.filter.categories", []);
  }, [getDictJson]);

  const licenseOptions = useMemo(() => {
    return getDictJson<string[]>("listing.rental.filter.licences", []);
  }, [getDictJson]);

  const billingTypesList = useMemo(() => {
    return getDictJson<string[]>("listing.rental.filter.billing.types", []);
  }, [getDictJson]);

  // 🟢 Fetch rental listings on component mount when the page opens
  useEffect(() => {
    fetchAircraftRentals().catch((err) => {
      console.error("Rental fetch error on page load:", err);
    });
  }, [fetchAircraftRentals]);

  const filteredRentals = useMemo(() => {
    if (isAircraftRentalsLoading) return [];

    let result = [...aircraftRentals];

    // 1. CĂUTARE TEXTUALĂ
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(r =>
        r.model?.toLowerCase().includes(q) ||
        r.registration?.toLowerCase().includes(q) ||
        r.baseAirport?.toLowerCase().includes(q) ||
        r.manufacturer?.toLowerCase().includes(q)
      );
    }

    // 2. FILTRU SUBCATEGORII
    if (selectedSubcategories.length > 0) {
      result = result.filter(r =>
        r.category && selectedSubcategories.includes(r.category)
      );
    }

    // 3. FILTRU TARIF ORAR
    if (maxRate) {
      result = result.filter(r => (Number(r.grossValue) || (r as any).hourlyRate || 0) <= Number(maxRate));
    }

    // 4. FILTRU LICENȚE
    if (selectedLicenses.length > 0) {
      result = result.filter(r =>
        (r as any).requiredLicenceTypes?.some((lic: string) => selectedLicenses.includes(lic))
      );
    }

    // 5. FILTRU EXPERIENȚĂ MINIMĂ
    if (maxExperience) {
      result = result.filter(r => ((r as any).minimumTotalExperience || 0) <= Number(maxExperience));
    }

    // 6. FILTRU VITEZĂ DE CROAZIERĂ
    if (minSpeed) {
      result = result.filter(r => ((r as any).cruiseSpeed || 0) >= Number(minSpeed));
    }

    // 7. FILTRU CERTIFICARE IFR
    if (ifrOnly) {
      result = result.filter(r => r.isIfrCertified === true);
    }

    // 8. FILTRU TIP COMBUSTIBIL
    if (selectedFuelTypes.length > 0) {
      result = result.filter(r => (r as any).fuelType && selectedFuelTypes.includes((r as any).fuelType));
    }

    // 9. FILTRU METODĂ FACTURARE
    if (billingMethod) {
      result = result.filter(r => r.billingMethod === billingMethod);
    }

    // 10. SORTARE
    switch (sort) {
      case "rate-asc":
        result.sort((a, b) => {
          const priceA = Number(a.grossValue || (a as any).hourlyRate) > 0 ? Number(a.grossValue || (a as any).hourlyRate) : Infinity;
          const priceB = Number(b.grossValue || (b as any).hourlyRate) > 0 ? Number(b.grossValue || (b as any).hourlyRate) : Infinity;
          return priceA - priceB;
        });
        break;
      case "rate-desc":
        result.sort((a, b) => {
          const priceA = Number(a.grossValue || (a as any).hourlyRate) > 0 ? Number(a.grossValue || (a as any).hourlyRate) : -1;
          const priceB = Number(b.grossValue || (b as any).hourlyRate) > 0 ? Number(b.grossValue || (b as any).hourlyRate) : -1;
          return priceB - priceA;
        });
        break;
      case "speed-desc":
        result.sort((a, b) => ((b as any).cruiseSpeed || 0) - ((a as any).cruiseSpeed || 0));
        break;
      case "newest":
        result.sort((a, b) => (Number(b.rentalId) || 0) - (Number(a.rentalId) || 0));
        break;
    }

    return result;
  }, [
    aircraftRentals,
    isAircraftRentalsLoading,
    search,
    selectedSubcategories,
    maxRate,
    sort,
    selectedLicenses,
    maxExperience,
    minSpeed,
    ifrOnly,
    selectedFuelTypes,
    billingMethod
  ]);

  return (
    <div className="min-h-screen bg-[#020617] text-slate-300 pb-20 font-sans">
      <InstrumentLoader visible={isAircraftRentalsLoading} />

      <div className="container max-w-7xl py-12 px-6">
        {/* HEADER */}
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 border-l-4 border-blue-600 pl-6">
          <div>
            <p className="text-blue-400 font-mono text-sm tracking-[0.2em] mb-1 uppercase">
              {getDictValue("listing.rental.header") || "Rental Hub // Aeronave de Închiriat"}
            </p>
            <h1 className="text-5xl font-black text-white tracking-tight italic uppercase">
              {title || getDictValue("listing.rental.title") || "AERONAVE & APARATE DE ZBOR"}
            </h1>
          </div>
          <div className="flex items-center gap-4 bg-slate-900/50 p-2 rounded-2xl border border-slate-800">
            <div className="text-right px-4 border-r border-slate-800">
              <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">
                {getDictValue("listing.rental.filter.match.txb") || "Unități găsite"}
              </p>
              <p className="text-blue-500 font-mono text-xl font-black">{filteredRentals.length}</p>
            </div>
            <LayoutGrid className="text-slate-700 mr-2" size={20} />
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* ASIDE FILTERS */}
          <aside className="lg:col-span-3 space-y-6 sticky top-24 h-[calc(100vh-120px)] overflow-y-auto no-scrollbar pb-10">
            <div className="bg-[#0F172A]/80 backdrop-blur-xl border border-slate-800 rounded-3xl p-6 shadow-2xl">

              {/* HEADER FILTRE & RESET */}
              <div className="flex items-center justify-between mb-6 border-b border-slate-800/50 pb-4">
                <div className="flex items-center gap-2">
                  <Filter size={16} className="text-blue-400" />
                  <h3 className="text-xs font-black uppercase tracking-widest text-white">
                    {getDictValue("listing.rental.filter.title") || "Filtre"}
                  </h3>
                </div>
                {(search || selectedSubcategories.length > 0 || selectedLicenses.length > 0 || maxRate || maxExperience || minSpeed || ifrOnly || billingMethod || selectedFuelTypes.length > 0) && (
                  <button
                    onClick={() => {
                      setSearch(""); setSelectedSubcategories([]); setSelectedLicenses([]);
                      setMaxRate(""); setMaxExperience(""); setMinSpeed("");
                      setIfrOnly(false); setBillingMethod(""); setSelectedFuelTypes([]);
                    }}
                    className="text-[10px] text-orange-500 font-bold uppercase hover:text-orange-400 flex items-center gap-1 transition-all"
                  >
                    <RotateCcw size={12} /> Reset System
                  </button>
                )}
              </div>

              <div className="space-y-8">
                {/* 1. SEARCH */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 block ml-1">
                    {getDictValue("listing.rental.filter.id.txb") || "Înmatriculare / Model / Bază"}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
                    <input
                      className="w-full h-11 pl-10 pr-4 rounded-xl bg-[#1E293B] border border-slate-700 text-sm text-white focus:border-blue-500 outline-none transition-all placeholder:text-slate-600"
                      placeholder={getDictValue("listing.rental.filter.id.placeholder") || "Ex: YR-ABC, LRBS..."}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>

                {/* 2. CATEGORII ACCORDEON */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3 block ml-1">
                    {getDictValue("listing.rental.filter.category.txb") || "Categorie"}
                  </label>
                  <div className="space-y-2">
                    {categoriesList.map((category) => {
                      const isExpanded = expandedCategory === category.label;

                      return (
                        <div key={category.label} className="group">
                          <button
                            onClick={() => setExpandedCategory(isExpanded ? null : category.label)}
                            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
                              isExpanded
                                ? "bg-slate-800 border-slate-600 text-white shadow-lg"
                                : "bg-slate-900/40 border-slate-800 text-slate-400 hover:border-slate-700"
                            }`}
                          >
                            <span className="text-[10px] font-black uppercase tracking-wider">{category.label}</span>
                            <Plane
                              size={12}
                              className={`transition-transform duration-300 ${isExpanded ? "rotate-90 text-blue-500" : "text-slate-600 group-hover:text-slate-400"}`}
                            />
                          </button>

                          {isExpanded && (
                            <div className="mt-2 grid grid-cols-1 gap-1.5 pl-2 animate-in fade-in slide-in-from-top-2 duration-300">
                              {category.options.map((opt) => {
                                const isSelected = selectedSubcategories.includes(opt.code);
                                return (
                                  <button
                                    key={opt.code}
                                    onClick={() => setSelectedSubcategories(prev =>
                                      isSelected ? prev.filter(c => c !== opt.code) : [...prev, opt.code]
                                    )}
                                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-[10px] font-bold uppercase border transition-all ${
                                      isSelected
                                        ? "bg-blue-600/20 border-blue-500 text-blue-400"
                                        : "bg-slate-950/40 border-slate-800 text-slate-500 hover:border-slate-700"
                                    }`}
                                  >
                                    <div className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.8)]" : "bg-slate-800"}`} />
                                    {opt.label}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* 3. PERFORMANȚĂ (IFR & SPEED) */}
                <div className="pt-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3 block ml-1">
                    {getDictValue("listing.rental.filter.performance.txb") || "Performanță & Avionică"}
                  </label>
                  <div className="space-y-4">
                    <button
                      onClick={() => setIfrOnly(!ifrOnly)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${
                        ifrOnly ? "bg-blue-500/10 border-blue-500 text-blue-400 shadow-[0_0_15px_rgba(59,130,246,0.2)]" : "bg-slate-900/40 border-slate-800 text-slate-500"
                      }`}
                    >
                      <span className="text-[10px] font-black uppercase">
                        {getDictValue("listing.rental.filter.ifr.txb") || "Certificare IFR"}
                      </span>
                      <ShieldCheck size={16} className={ifrOnly ? "text-blue-400" : "text-slate-700"} />
                    </button>

                    <div>
                      <div className="flex justify-between text-[9px] font-bold text-slate-500 uppercase mb-2">
                        <span>{getDictValue("listing.rental.filter.speed.txb") || "Viteză de croazieră (Min)"}</span>
                        <span className="text-blue-400 font-mono">{minSpeed || 60} KTS</span>
                      </div>
                      <input
                        type="range" min="60" max="250" step="5"
                        className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-blue-500"
                        value={minSpeed || 60}
                        onChange={(e) => setMinSpeed(Number(e.target.value))}
                      />
                    </div>
                  </div>
                </div>

                {/* 4. LICENȚE */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3 block ml-1">
                    {getDictValue("listing.rental.filter.licences.txb") || "Licențe Acceptate"}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {licenseOptions.map((lic) => {
                      const isSelected = selectedLicenses.includes(lic);
                      return (
                        <button
                          key={lic}
                          onClick={() => setSelectedLicenses(prev =>
                            isSelected ? prev.filter(l => l !== lic) : [...prev, lic]
                          )}
                          className={`flex items-center justify-center p-2 rounded-lg border text-[8px] font-black transition-all ${
                            isSelected
                              ? "bg-blue-500/20 border-blue-500 text-blue-400"
                              : "bg-slate-900/40 border-slate-800 text-slate-500 hover:border-slate-700"
                          }`}
                        >
                          {lic}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 5. EXPERIENȚĂ SLIDER */}
                <div>
                  <div className="flex justify-between items-center mb-2 ml-1">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      {getDictValue("listing.rental.filter.experience.txb") || "Experiență necesară (Max)"}
                    </label>
                    <span className="text-[10px] font-mono text-blue-400">{maxExperience || 0} HRS</span>
                  </div>
                  <input
                    type="range" min="0" max="1000" step="10"
                    className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-blue-500"
                    value={maxExperience || 0}
                    onChange={(e) => setMaxExperience(Number(e.target.value))}
                  />
                  <p className="text-[8px] text-slate-600 uppercase mt-2 italic">*Afișează avioanele pe care le poți zbura cu experiența ta.</p>
                </div>

                {/* 6. HOURLY RATE */}
                <div>
                  <div className="flex justify-between items-center mb-2 ml-1">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      {getDictValue("listing.rental.filter.budget.txb") || "Buget (EUR/H)"}
                    </label>
                    <span className="text-sm font-black text-white">{maxRate ? `${maxRate}€` : "∞"}</span>
                  </div>
                  <input
                    type="range" min="50" max="1000" step="25"
                    className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-blue-500"
                    value={maxRate || 1000}
                    onChange={(e) => setMaxRate(Number(e.target.value))}
                  />
                </div>

                {/* 7. FACTURARE & COMBUSTIBIL */}
                <div className="space-y-4 pt-4 border-t border-slate-800/50">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 block">
                      {getDictValue("listing.rental.filter.billing.txb") || "Facturare"}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {billingTypesList.map(method => (
                        <button
                          key={method}
                          onClick={() => setBillingMethod(billingMethod === method ? "" : method)}
                          className={`py-2 rounded-lg text-[9px] font-black uppercase border transition-all ${
                            billingMethod === method ? "bg-blue-600 border-blue-400 text-white" : "bg-slate-900/40 border-slate-800 text-slate-500"
                          }`}
                        >
                          {method}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 block">Combustibil</label>
                    <div className="flex flex-wrap gap-2">
                      {fuelOptions.map(fuel => (
                        <button
                          key={fuel}
                          onClick={() => setSelectedFuelTypes(prev =>
                            prev.includes(fuel) ? prev.filter(f => f !== fuel) : [...prev, fuel]
                          )}
                          className={`px-2 py-1.5 rounded-lg text-[9px] font-bold border transition-all ${
                            selectedFuelTypes.includes(fuel) ? "border-blue-400 text-blue-400 bg-blue-400/5" : "border-slate-800 text-slate-500"
                          }`}
                        >
                          {fuel}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* MATCH COUNTER */}
                <div className="pt-4 border-t border-slate-800/50">
                  <div className="bg-slate-900/80 rounded-2xl p-4 border border-slate-800 flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${filteredRentals.length > 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                      <Activity size={16} className={filteredRentals.length > 0 ? 'text-emerald-500' : 'text-red-500'} />
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 font-bold uppercase">
                        {getDictValue("listing.rental.filter.match.txb") || "Unități găsite"}
                      </p>
                      <p className="text-sm font-black text-white italic">
                        {filteredRentals.length} {getDictValue("listing.rental.filter.match.results") || "Rezultate"}
                      </p>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </aside>

          {/* FLEET GRID */}
          <main className="lg:col-span-9 space-y-6">
            {isAircraftRentalsLoading ? (
              <div className="flex items-center justify-center py-32"></div>
            ) : filteredRentals.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-32 bg-slate-900/10 rounded-[3rem] border border-dashed border-slate-800">
                <Plane size={32} className="text-slate-700 -rotate-45 mb-6" />
                <h4 className="text-white font-black uppercase tracking-widest mb-2">
                  {getDictValue("home.search.noResults") || "Nu s-au găsit anunțuri"}
                </h4>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
                {filteredRentals.map(rental => (
                  <div
                    key={rental.rentalId}
                    onClick={() => navigateTo("rental-details", {
                      rentalId: rental.rentalId,
                      aircraftRental: rental
                    })}
                    className="cursor-pointer transition-transform hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <RentalProductCard rental={rental} />
                  </div>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AircraftRentalCategoryPage;